import React, { useMemo, useState } from "react";
import { Breadcrumbs, LoadingOverlay, Stack, Text } from "@mantine/core";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import PageContainer from "../../../themes/components/PageContainer";
import PageHeading from "../../../themes/components/PageHeading";
import BatchClockForm from "../components/BatchClockForm";
import { useLocationAttendanceApiService } from "../services/api/LocationAttendanceApiService";
import { useShiftSiteAttendanceApiService } from "../services/api/ShiftSiteAttendanceApiService";
import { useBreadcrumbStyles } from "../styles/sharedStyles";
import AccessGuard from "../../../common/uac/AccessGuard";

const BatchClockPageInner: React.FC = () => {
  const { classes: bcClasses } = useBreadcrumbStyles();
  const navigate = useNavigate();
  const { locationId: routeLocationId } = useParams<{ locationId: string }>();

  const today = dayjs().format("YYYY-MM-DD");

  // The roster (shift location) currently being clocked. Defaults to the route
  // location (the one the "Clock In/Out Employees" button preselected), but the
  // user can switch to any sibling roster in the same site — without this an
  // officer whose clock-in landed on a different roster can't be clocked out.
  const [selectedLocationId, setSelectedLocationId] = useState(
    routeLocationId || ""
  );

  const { data, isLoading } = useLocationAttendanceApiService({
    locationId: selectedLocationId,
    date: today,
    enabled: Boolean(selectedLocationId),
  });

  const location = data?.location;
  const siteId = location?.site || null;
  const locationBackUrl = siteId
    ? `/time-attendance/shift-site/${siteId}`
    : `/time-attendance/${selectedLocationId}`;
  const allRecords = data?.records || [];
  const shiftConfigs = (location?.shift_configs || []).filter(
    (c) => c.start_time && c.end_time && c.is_required_on_site
  );

  // Sibling rosters in the same site, for the roster switcher. Cache-warm when
  // the user arrived from the site view. Always include the current roster so
  // the switcher's value resolves even before the site list loads.
  const { data: siteData } = useShiftSiteAttendanceApiService({
    siteId: siteId || "",
    date: today,
    enabled: Boolean(siteId),
  });
  const rosters = useMemo(() => {
    const list = (siteData?.shift_locations || []).map((g) => ({
      id: g.location.id,
      name: g.location.name,
    }));
    if (location && !list.some((r) => r.id === location.id)) {
      list.unshift({ id: location.id, name: location.name });
    }
    return list;
  }, [siteData, location]);

  const handleSuccess = (): void => {
    navigate(locationBackUrl);
  };

  const handleCancel = (): void => {
    navigate(locationBackUrl);
  };

  return (
    <PageContainer>
      <LoadingOverlay visible={isLoading} />

      <PageHeading title="Time & Attendance" />

      {/* Breadcrumbs */}
      <Breadcrumbs
        separator=">"
        classNames={{
          root: "mt-2 mb-6",
          breadcrumb: "hover:no-underline text-sm",
          separator: "text-gray-400",
        }}
      >
        <Text
          className={bcClasses.breadcrumbLink}
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/time-attendance")}
        >
          Time &amp; Attendance
        </Text>
        <Text
          className={bcClasses.breadcrumbLink}
          style={{ cursor: "pointer" }}
          onClick={() => navigate(locationBackUrl)}
        >
          {location?.name || "..."}
        </Text>
        <Text className={bcClasses.breadcrumbCurrent}>
          Clock In/Out Employees
        </Text>
      </Breadcrumbs>

      <Stack spacing="md">
        <Text size="sm" color="dimmed">
          {dayjs().format("dddd, MMMM D, YYYY")}
        </Text>

        <BatchClockForm
          key={selectedLocationId}
          employees={allRecords}
          shiftConfigs={shiftConfigs}
          locationId={selectedLocationId}
          rosters={rosters}
          onRosterChange={setSelectedLocationId}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </Stack>
    </PageContainer>
  );
};

const BatchClockPage: React.FC = () => (
  <AccessGuard>
    <BatchClockPageInner />
  </AccessGuard>
);

export default BatchClockPage;
